import React, { useState, useEffect } from 'react';
import { Select, DatePicker, Button, Card, Row, Col, Typography, Empty, message, Input, Spin, Modal } from 'antd';
import {  
  FileImageOutlined, 
  FilterOutlined, 
  VideoCameraOutlined, 
  SearchOutlined,
  CalendarOutlined,
  UserOutlined,
  CommentOutlined,
  EyeOutlined,
  DownloadOutlined,
  FullscreenOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from '../../services/axios';
import dayjs from 'dayjs';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

const ViewPhotos = () => {
  const [dateRange, setDateRange] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [allPhotos, setAllPhotos] = useState([]);
  const [mediaType, setMediaType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);

  const user = localStorage.getItem('user');
  const role = JSON.parse(localStorage.getItem('role'))[0];
  const projectName = JSON.parse(localStorage.getItem('Project'));

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/get-all-procams', {
        user,
        role,
        projectName,
      });  
      let photos = response.data.procams || [];
      setAllPhotos(photos);
      applyFilters(photos);
    } catch (err) {
      console.error('Fetch error:', err);
      if (err.response && err.response.data && err.response.data.msg) {
        message.warning(err.response.data.msg);
      } else if (err.response?.data?.userName) {
        message.warning(err.response.data.userName[0]);
      } else if (err.response?.status === 401) {
        message.error('User not authorized');
      } else {
        message.error('Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (photos = allPhotos) => {
    let filtered = [...photos];
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter(photo => {
        const photoDate = dayjs(photo.date);
        return photoDate.isAfter(start.subtract(1, 'day')) && photoDate.isBefore(end.add(1, 'day'));
      });
    }
    if (mediaType !== 'all') {
      filtered = filtered.filter(item => {
        const isVideoFile = isVideo(item);
        return mediaType === 'video' ? isVideoFile : !isVideoFile;
      });
    }
    if (searchQuery.trim()) {
      filtered = filtered.filter(item => 
        (item.comment && item.comment.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.uploadedBy && item.uploadedBy.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    setFilteredPhotos(filtered);
  };
  
  useEffect(() => {
    fetchPhotos();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [dateRange, mediaType, searchQuery, allPhotos]);

  const isVideo = (item) => {
    if (!item) return false;
    if (item.mediaType === 'video') return true;
    if (typeof item.photoUrl === 'string') {
      const url = item.photoUrl.toLowerCase();
      return url.endsWith('.mp4') || 
             url.endsWith('.mov') || 
             url.endsWith('.avi') ||
             url.endsWith('.webm') ||
             url.endsWith('.mkv');
    }
    if (typeof item.videoUrl === 'string') {
      return true;
    }
    return false;
  };

  const getMediaUrl = (item) => {
    if (!item) return '';
    const url = item.videoUrl || item.photoUrl || '';
    return url;
  };

  const groupedPhotos = filteredPhotos.reduce((acc, photo) => {
    const month = moment(photo.uploadedDate || photo.date).format('MMMM YYYY');
    if (!acc[month]) acc[month] = [];
    acc[month].push(photo);
    return acc;
  }, {});

  const handleMediaClick = (item) => {
    setSelectedMedia(item);
  };

  const clearFilters = () => {
    setDateRange([]);
    setMediaType('all');
    setSearchQuery('');
  };

  const handleImageError = (e) => {
    console.error('Image failed to load:', e.target.src);
    message.error('Failed to load image');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <style jsx>{`
        .media-card {
          transition: all 0.3s ease;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }
        .media-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }
        .media-container {
          position: relative;
          overflow: hidden;
          height: 200px;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        }
        .media-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.3) 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }
        .media-card:hover .media-overlay {
          opacity: 1;
        }
        .filter-card {
          background: white;
          border-radius: 20px;
          padding: 16px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
          border: 1px solid #f0f0f0;
        }
        .month-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 12px 16px;
          border-radius: 16px;
          margin-bottom: 16px;
          box-shadow: 0 4px 16px rgba(102, 126, 234, 0.2);
        }
        .stats-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 12px;
          border-radius: 16px;
          text-align: center;
          box-shadow: 0 4px 16px rgba(102, 126, 234, 0.2);
        }
        .search-input {
          border-radius: 12px !important;
          border: 2px solid #e8e8e8 !important;
          padding: 8px 12px !important;
          font-size: 14px !important;
        }
        .search-input:focus {
          border-color: #667eea !important;
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1) !important;
        }
        .video-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(255, 77, 79, 0.9);
          color: white;
          padding: 4px 8px;
          border-radius: 8px;
          font-size: 12px;
          backdrop-filter: blur(4px);
          pointer-events: none;
        }
        .photo-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(82, 196, 26, 0.9);
          color: white;
          padding: 4px 8px;
          border-radius: 8px;
          font-size: 12px;
          backdrop-filter: blur(4px);
          pointer-events: none;
        }
        .modal-media {
          max-width: 90vw;
          max-height: 90vh;
          object-fit: contain;
          border-radius: 8px;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="filter-card mb-8">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-3">
              <FilterOutlined className="text-xl text-blue-500" />
              <Title level={4} className="m-0 text-gray-800">Filters</Title>
            </div>
            <Button type="link" onClick={clearFilters} className="text-blue-500">
              Clear All
            </Button>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={8}>
              <div className="space-y-1">
                <Text strong className="text-gray-700">Search</Text>
                <Input
                  placeholder="Search by comment or uploader..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="search-input"
                  allowClear
                />
              </div>
            </Col>

            <Col xs={24} lg={8}>
              <div className="space-y-1">
                <Text strong className="text-gray-700">Date Range</Text>
                <RangePicker
                  style={{ width: '100%', height: '40px', borderRadius: '12px' }} 
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                />
              </div>
            </Col>

            <Col xs={24} lg={4}>
              <div className="space-y-1">
                <Text strong className="text-gray-700">Media Type</Text>
                <Select
                  value={mediaType}
                  onChange={setMediaType}
                  style={{ width: '100%', height: '40px' }} 
                  className="rounded-xl"
                >
                  <Option value="all">All Media</Option>
                  <Option value="photo">Photos Only</Option>
                  <Option value="video">Videos Only</Option>
                </Select>
              </div>
            </Col>

            <Col xs={24} lg={4}>
              <div className="space-y-1">
                <Text strong className="text-gray-700">Actions</Text>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={fetchPhotos}
                  loading={loading}
                  style={{ width: '100%', height: '40px', borderRadius: '12px' }} 
                >
                  Refresh
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {/* Stats */}
        <Row gutter={[16, 16]} className="mb-8">
          <Col xs={8} sm={8}>
            <div className="stats-card">
              <div className="text-xl font-bold"> 
                {filteredPhotos.length}
              </div>
              <div className="text-xs opacity-90">
                Total Items
              </div>
            </div>
          </Col>
          <Col xs={8} sm={8}>
            <div className="stats-card">
              <div className="text-xl font-bold text-green-200">
                {filteredPhotos.filter(item => !isVideo(item)).length}
              </div>
              <div className="text-xs opacity-90"> 
                Photos
              </div>
            </div>
          </Col>
          <Col xs={8} sm={8}>
            <div className="stats-card">
              <div className="text-xl font-bold text-red-200">
                {filteredPhotos.filter(item => isVideo(item)).length}
              </div>
              <div className="text-xs opacity-90">
                Videos
              </div>
            </div>
          </Col>
        </Row>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-20">
            <Spin size="large" className="mb-4" />
            <div className="text-gray-600">Loading media files...</div>
          </div>
        )}

        {/* Gallery Content */}
        {!loading && (
          <>
            {Object.keys(groupedPhotos).length === 0 ? (
              <div className="text-center py-20">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <div className="space-y-4">
                      <Title level={4} className="text-gray-400">No media files found</Title>
                      <Text className="text-gray-500">
                        Try adjusting your filters or check if there are any uploaded files
                      </Text>
                    </div>
                  }
                />
              </div>
            ) : (
              Object.entries(groupedPhotos).map(([month, photos]) => (
                <div key={month} className="mb-12">
                  <div className="month-header">
                    <div className="flex items-center justify-between">
                      <div>
                        <Title level={3} className="m-0 text-white font-bold">
                          {month}
                        </Title>
                        <Text className="text-white opacity-90">
                          {photos.length} {photos.length === 1 ? 'item' : 'items'}
                        </Text>
                      </div>
                      <CalendarOutlined className="text-2xl text-white opacity-75" />
                    </div>
                  </div>

                  <Row gutter={[16, 16]}>
                    {photos.map((item) => {
                      const isVideoFile = isVideo(item);
                      const mediaUrl = getMediaUrl(item);

                      return (
                        <Col key={item._id} xs={24} sm={12} md={6} lg={4}>
                          <Card
                            className="media-card"
                            bodyStyle={{ padding: '12px' }}
                            cover={
                              <div className="media-container">
                                {isVideoFile ? (
                                  <>
                                    <video
                                      controls
                                      className="w-full h-full object-cover cursor-pointer"
                                      poster={item.thumbnailUrl}
                                      onClick={() => handleMediaClick(item)}
                                    >
                                      <source src={mediaUrl} type="video/mp4" />
                                    </video>
                                    <div className="video-badge">
                                      <VideoCameraOutlined className="mr-1" />
                                      Video
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <img
                                      alt="Project Media"
                                      src={mediaUrl}
                                      className="w-full h-full object-cover cursor-pointer"
                                      onClick={() => handleMediaClick(item)}
                                      onError={handleImageError}
                                    />
                                    <div className="photo-badge">
                                      <FileImageOutlined className="mr-1" />
                                      Photo
                                    </div>
                                  </>
                                )}
                                <div className="media-overlay">
                                  <div className="absolute bottom-4 right-4">
                                    <FullscreenOutlined className="text-white text-xl" />
                                  </div>
                                </div>
                              </div>
                            }
                          >
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                <div className="flex items-center space-x-1">
                                  <CalendarOutlined />
                                  <span>{moment(item.uploadedDate || item.date).format('MMM DD, YYYY')}</span>
                                </div>
                              </div>
                              {item.uploadedBy && (
                                <div className="flex items-center space-x-2 text-sm">
                                  <UserOutlined className="text-blue-500" />
                                  <span className="font-medium text-gray-700">{item.uploadedBy}</span>
                                </div>
                              )}
                              {item.comment && (
                                <div className="flex items-start space-x-2 h-12">
                                  <CommentOutlined className="text-gray-400 mt-1 flex-shrink-0" />
                                  <p 
                                    className="text-gray-600 text-sm leading-relaxed line-clamp-2"
                                    title={item.comment}
                                  >
                                    {item.comment}
                                  </p>
                                </div>
                              )}
                              <div className="text-xs text-gray-400 mt-2">
                                {moment(item.uploadedDate || item.date).format('HH:mm')}
                              </div>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              ))
            )}
          </>
        )}

        {/* Media Modal */}
        {selectedMedia && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedMedia(null)}
          >
            <div 
              className="relative 
              w-[900px] h-[700px] 
              md:w-[750px] md:h-[550px] 
              sm:w-[600px] sm:h-[450px] 
              bg-gray-900 rounded-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {isVideo(selectedMedia) ? (
                <video
                  controls
                  autoPlay
                  className="w-full h-full object-contain"
                  poster={selectedMedia.thumbnailUrl}
                  onError={() => {
                    console.error('Video failed to load:', getMediaUrl(selectedMedia));
                    message.error('Failed to load video');
                  }}
                >
                  <source src={getMediaUrl(selectedMedia)} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : getMediaUrl(selectedMedia) ? (
                <img
                  src={getMediaUrl(selectedMedia)}
                  alt="Enlarged view"
                  className="w-full h-full object-contain"
                  onError={handleImageError}
                />
              ) : (
                <div className="flex items-center justify-center w-full h-full text-white">
                  <Text className="text-red-500">Error: No valid media URL found</Text>
                </div>
              )}
              <Button
                type="primary"
                shape="circle"
                size="large"
                className="absolute top-4 right-4 bg-black bg-opacity-50 border-none text-white hover:bg-opacity-70"
                onClick={() => setSelectedMedia(null)}
              >
                ×
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewPhotos;